import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  /** Unique field name (e.g. "clientSecret") for aria + form bookkeeping. */
  name: string;
  /** Whether the parent record already has a stored secret (edit mode with existing). */
  hasStoredValue: boolean;
  /** Label shown above the field. */
  label: string;
  /** Marks the field required (drives red border for empty-on-create). */
  required?: boolean;
  /** Invoked on every change with the raw input value. */
  onChange: (value: string) => void;
  /** Invoked when the user confirms an intentional clear. Parent reads this before sending "". */
  onConfirmClear?: () => void;
}

export interface SensitiveFieldHandle {
  /** True once the user has edited the field (pristine → keep existing; touched → send value). */
  hasBeenTouched: () => boolean;
  /** Resets touched state — call after a successful save. */
  reset: () => void;
}

/**
 * Masked password-style input with three states:
 *   1. hasStoredValue && !touched → placeholder "•••••••• (stored)", empty value; parent omits from PATCH
 *   2. hasStoredValue && touched && value=="" → prompt caller to confirm intentional clear
 *   3. value non-empty → send value on save
 *
 * Parent reads `hasBeenTouched()` via ref to decide whether to include the field in the PATCH payload.
 */
export const SensitiveField = forwardRef<SensitiveFieldHandle, Props>(function SensitiveField(
  { name, hasStoredValue, label, required, onChange, onConfirmClear },
  ref,
) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [visible, setVisible] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    hasBeenTouched: () => touched,
    reset: () => {
      setValue('');
      setTouched(false);
      setConfirmingClear(false);
    },
  }));

  const handleChange = (next: string) => {
    setTouched(true);
    setValue(next);
    onChange(next);
    if (hasStoredValue && next === '') {
      setConfirmingClear(true);
    } else {
      setConfirmingClear(false);
    }
  };

  return (
    <label className="block">
      <span className="text-xs text-neutral-600">
        {label}{required ? ' *' : ''}
      </span>
      <div className="mt-1 relative">
        <input
          ref={inputRef}
          name={name}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={hasStoredValue && !touched ? t('App.Admin.Booksellers.SensitiveField.StoredPlaceholder') : ''}
          autoComplete="new-password"
          className="border border-neutral-300 rounded px-2 py-1 text-sm w-full pr-16"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-900"
          aria-label={visible ? t('App.Admin.Booksellers.SensitiveField.HideAriaLabel', { label }) : t('App.Admin.Booksellers.SensitiveField.ShowAriaLabel', { label })}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {hasStoredValue && !touched && (
        <p className="text-xs text-neutral-400 mt-1">
          {t('App.Admin.Booksellers.SensitiveField.KeepBlankHint')}
        </p>
      )}
      {confirmingClear && (
        <div className="text-xs text-amber-700 mt-1 flex items-center gap-2">
          <span>{t('App.Admin.Booksellers.SensitiveField.ClearWarning')}</span>
          <button
            type="button"
            onClick={() => {
              setConfirmingClear(false);
              onConfirmClear?.();
            }}
            className="underline"
          >
            {t('App.Admin.Booksellers.SensitiveField.ConfirmClear')}
          </button>
        </div>
      )}
    </label>
  );
});
